import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchGlobal, createSampleLeague } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * CommandPalette — global Cmd/Ctrl-K launcher. Searches players, leagues,
 * teams in parallel, plus a fixed list of "jump-to" pages so power users
 * can navigate anywhere in 2 keystrokes.
 *
 * Keyboard:
 *   Cmd/Ctrl-K → open
 *   ESC        → close
 *   ↑ / ↓      → move highlight
 *   Enter      → pick highlighted
 */
const PAGES = [
  { id: "page:dashboard", group: "PAGES", title: "Dashboard",     icon: "fa-house",      to: "/dashboard" },
  { id: "page:leagues",   group: "PAGES", title: "Leagues",       icon: "fa-trophy",     to: "/leagues" },
  { id: "page:players",   group: "PAGES", title: "Players",       icon: "fa-users",      to: "/players" },
  { id: "page:teams",     group: "PAGES", title: "Teams",         icon: "fa-shield-halved", to: "/teams" },
  { id: "page:rankings",  group: "PAGES", title: "Rankings",      icon: "fa-medal",      to: "/rankings" },
  { id: "page:lfg",       group: "PAGES", title: "Looking for Group", icon: "fa-magnifying-glass", to: "/lfg" },
  { id: "page:season",    group: "PAGES", title: "Season Pass",   icon: "fa-bolt",       to: "/season-pass" },
  { id: "page:pricing",   group: "PAGES", title: "Pricing",       icon: "fa-credit-card", to: "/pricing" },
  { id: "page:contact",   group: "PAGES", title: "Contact",       icon: "fa-envelope",   to: "/contact" },
  { id: "page:developers",      group: "PAGES", title: "Developer Portal", icon: "fa-code",   to: "/developers" },
  { id: "page:dev-settings",    group: "PAGES", title: "API Keys & Webhooks", icon: "fa-key", to: "/settings/developer" },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ players: [], leagues: [], teams: [] });
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const nav = useNavigate();
  const { user, logout } = useAuth();

  // Quick actions are built per render so the user context (auth + name)
  // can drive which ones are available + their `to` URLs.
  const ACTIONS = useMemo(() => {
    const list = [];
    if (user) {
      list.push(
        { id: "act:post-lfg",   group: "QUICK ACTIONS", title: "Post LFG signal",        icon: "fa-bullhorn",   to: "/lfg" },
        { id: "act:open-inbox", group: "QUICK ACTIONS", title: "Open inbox",             icon: "fa-envelope",
          run: () => { nav("/dashboard"); setTimeout(() => window.dispatchEvent(new CustomEvent("dash:open-inbox")), 200); } },
        { id: "act:friends",    group: "QUICK ACTIONS", title: "Open Friends list",      icon: "fa-user-group",
          run: () => { nav("/dashboard"); setTimeout(() => window.dispatchEvent(new CustomEvent("dash:open-friends")), 200); } },
        { id: "act:schedule",   group: "QUICK ACTIONS", title: "Schedule a match",       icon: "fa-calendar-plus",
          run: () => { nav("/dashboard"); setTimeout(() => window.dispatchEvent(new CustomEvent("dash:schedule-match")), 200); } },
        { id: "act:profile",    group: "QUICK ACTIONS", title: "View my profile",        icon: "fa-id-badge",   to: `/players/${user.user_id}` },
        { id: "act:season",     group: "QUICK ACTIONS", title: "Open Season Pass",       icon: "fa-bolt",       to: "/season-pass" },
        { id: "act:demo",       group: "QUICK ACTIONS", title: "Spawn demo league + bots", icon: "fa-robot",
          run: async () => { try { await createSampleLeague(); window.dispatchEvent(new CustomEvent("dash:reload")); } catch {} } },
        { id: "act:signout",    group: "QUICK ACTIONS", title: "Sign out",               icon: "fa-right-from-bracket",
          run: () => logout?.() },
      );
    } else {
      list.push(
        { id: "act:signin",     group: "QUICK ACTIONS", title: "Sign in",                icon: "fa-right-to-bracket",
          run: () => window.dispatchEvent(new CustomEvent("auth:open-signin")) },
        { id: "act:pricing",    group: "QUICK ACTIONS", title: "See pricing",            icon: "fa-credit-card", to: "/pricing" },
      );
    }
    return list;
  }, [user, nav, logout]);

  // Global open shortcut.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Autofocus when opened.
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(""); setActive(0); }
  }, [open]);

  // Debounced search.
  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => {
      searchGlobal(q, 6).then(setResults).catch(() => {});
    }, q ? 150 : 0);
    return () => clearTimeout(id);
  }, [q, open]);

  const matchedPages = useMemo(() => {
    const rx = (q || "").trim().toLowerCase();
    if (!rx) return PAGES;
    return PAGES.filter((p) => p.title.toLowerCase().includes(rx));
  }, [q]);

  const matchedActions = useMemo(() => {
    const rx = (q || "").trim().toLowerCase();
    if (!rx) return ACTIONS;
    return ACTIONS.filter((a) => a.title.toLowerCase().includes(rx));
  }, [q, ACTIONS]);

  const items = useMemo(() => {
    const rows = [];
    matchedActions.forEach((a) => rows.push({ ...a, kind: "action" }));
    matchedPages.forEach((p) => rows.push({ ...p, kind: "page" }));
    (results.players || []).forEach((p) => rows.push({
      id: `pl:${p.user_id}`, kind: "player", group: "PLAYERS",
      title: p.name, sub: p.game || "—",
      icon: "fa-user", to: `/players/${p.user_id}`,
    }));
    (results.leagues || []).forEach((l) => rows.push({
      id: `lg:${l.league_id}`, kind: "league", group: "LEAGUES",
      title: l.name, sub: `${l.game} · ${l.league_type}`,
      icon: "fa-trophy", to: `/${l.slug || l.league_id}`,
    }));
    (results.teams || []).forEach((t) => rows.push({
      id: `tm:${t.team_id}`, kind: "team", group: "TEAMS",
      title: t.name, sub: `${t.tag || ""} · MMR ${t.mmr || 1500}`,
      icon: "fa-shield-halved", to: t.league_id ? `/teams/${t.team_id}` : "#",
    }));
    return rows;
  }, [matchedPages, results]);

  const pick = useCallback((i) => {
    const it = items[i];
    if (!it) return;
    setOpen(false);
    if (it.run) {
      try { it.run(); } catch { /* user-facing toast handled by callers */ }
    } else if (it.to) {
      nav(it.to);
    }
  }, [items, nav]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(items.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(active); }
  };

  if (!open) return null;
  // Listed in the order we want groups to appear (QUICK ACTIONS first,
  // then PAGES, then entity hits). The flatIdx tracking below relies on
  // this fixed traversal order.
  const groupOrder = ["QUICK ACTIONS", "PAGES", "PLAYERS", "LEAGUES", "TEAMS"];
  const grouped = items.reduce((acc, r) => {
    acc[r.group] = acc[r.group] || [];
    acc[r.group].push(r);
    return acc;
  }, {});
  let flatIdx = -1;

  return (
    <div data-testid="cmdk-backdrop"
         onClick={() => setOpen(false)}
         className="fixed inset-0 z-[150] bg-black/80 backdrop-blur-sm flex items-start justify-center pt-24 px-4">
      <div data-testid="cmdk-modal"
           onClick={(e) => e.stopPropagation()}
           className="w-full max-w-2xl rounded-2xl border-2 border-[#14b8a6]/50 bg-[#0d0521]/95 shadow-[0_0_60px_rgba(20, 184, 166,0.4)] overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
          <i className="fas fa-magnifying-glass text-[#14b8a6]" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => { setQ(e.target.value); setActive(0); }}
            onKeyDown={onKeyDown}
            placeholder="Search players, leagues, teams… or type a page name"
            data-testid="cmdk-input"
            className="flex-1 bg-transparent outline-none text-white placeholder-white/30"
          />
          <kbd className="text-[9px] tracking-widest px-1.5 py-0.5 rounded bg-white/5 border border-white/15 text-white/55">ESC</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto" data-testid="cmdk-results">
          {items.length === 0 ? (
            <p className="text-[12px] text-white/40 text-center py-8">No matches.</p>
          ) : groupOrder.filter((g) => grouped[g]).map((group) => (
            <div key={group}>
              <p className="px-4 pt-3 pb-1 text-[9px] tracking-[3px] text-white/40 font-bold">{group}</p>
              {grouped[group].map((r) => {
                flatIdx += 1;
                const isActive = flatIdx === active;
                const i = flatIdx;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => pick(i)}
                    onMouseEnter={() => setActive(i)}
                    data-testid={`cmdk-item-${r.id}`}
                    className={`w-full text-left flex items-center gap-3 px-4 py-2.5 ${
                      isActive ? "bg-[#14b8a6]/25" : "hover:bg-white/5"
                    }`}
                  >
                    <i className={`fas ${r.icon} text-[#a3a3ac] w-4`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{r.title}</p>
                      {r.sub && <p className="text-[10px] text-white/45 truncate">{r.sub}</p>}
                    </div>
                    {isActive && <i className="fas fa-arrow-right text-[#14b8a6] text-[11px]" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="px-4 py-2 border-t border-white/10 flex items-center gap-3 text-[10px] text-white/40">
          <kbd className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10">↑↓</kbd> navigate
          <kbd className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10">↵</kbd> open
          <kbd className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10">⌘K</kbd> toggle
        </div>
      </div>
    </div>
  );
}
